import type { UseFetchOptions } from 'nuxt/app'
import crypto from '@/utils/crypto'
import { Logger } from '~/utils/logger'
import { useRequestEvent } from '#app'
import {singleSlashPath} from '@/utils/jsonUtil'

type Methods = 'GET' | 'POST' | 'DELETE' | 'PUT'
const BASE_URL = '/api'

// / 定义一个去除ref属性的函数
export function removeRefs(obj: any) {
  const newObj: any = {}
  for (const key in obj) {
    if (typeof obj[key]?.value !== 'undefined') {
      newObj[key] = obj[key]?.value
    } else {
      newObj[key] = obj[key]
    }
  }
  return newObj
}

function isM3u8(url: any) {
  return url?.includes('m3u8')
}

export interface IResultData<T> {
  code: number
  data: T
  msg: string
}

interface Options<T> extends UseFetchOptions<T> {
  cryptoData?: boolean
  isJson?: boolean
}
export async function fetchJson(url: string) {
  const response = await fetch(singleSlashPath(url))
  const data = await response.json()
  console.log('JSON_URL:', url)
  console.log('JSON_DATA:', cryptoAES.decryptJson(data))
  if (cryptoAES.decryptJson(data) == '[]') return []
  return cryptoAES.decryptJson(data)
}
const sysFlag = process.server ? 'Server' : 'Client'

class HttpRequest {
  async request<T = any>(url: string, method: Methods, reqData: any, options?: Options<T>) {
    const globalStor = useGlobalStore()
    const { tenantId, webSiteId, appConfig }: any = storeToRefs(useGlobalStore())
    const { userInfo }: any = storeToRefs(useAuthStore())
    // console.log(userInfo.value.access_token,'ddd')
    const event = useRequestEvent()
    const headers = options?.headers || {}
    return new Promise(async (resolve, reject) => {
      // url包含以下关键字，强制不加密
      let forceNotCrypto = false
      let notCryptoKeys = ['media-auth/app/oauth/token', 'upload']
      notCryptoKeys.forEach((item) => {
        if (url.includes(item)) {
          forceNotCrypto = true
        }
      })
      const newOptions: Options<T> = {
        timeout: 10000,
        baseURL: options?.isJson ? '' : BASE_URL,
        method: method,
        // cryptoData: isM3u8(url) ? false : false, //默认开启data加密
        cryptoData: isM3u8(url) || forceNotCrypto ? false : true, //默认开启data加密
        ...options,
        onRequest({ request, options }: any) {
          // 设置请求头
          options.headers = options.headers || {}
          options.headers = {
            'Tenant-Id': tenantId.value,
            'Website-Id': webSiteId.value,
            // 当前浏览器域名
            'Media-Domain': location.origin
          }
          options.headers['Content-Type'] = 'application/json'
          options.headers['Channel-Id'] = appConfig.value?.channelId
          options.headers['Project-Id'] = appConfig.value?.projectIds
          options.headers['Device-Type'] = getPlatform()

          // 永远写死
          options.headers['Authorization'] = 'Basic YXBwOmFwcF9zZWNyZXQ='
          options.headers = Object.assign(options.headers, headers)
          if (userInfo?.value?.access_token) {
            options.headers['Media-Auth'] = 'bearer ' + userInfo.value.access_token
          }
          // console.log('请求头：', options.headers)
          if (globalStor.isDev) {
            console.log(`${sysFlag}端-接口请求：`, {
              request: request,
              params: options.params,
              method: options.method,
              headers: options.headers,
              body: options.body
            })
          }
          if (process.server) {
            Logger.info(
              `${event?.context.$routeId}-${sysFlag}端-接口发起请求：`,
              JSON.stringify({
                request: request,
                params: options.params,
                method: options.method,
                headers: options.headers,
                body: options.body
              })
            )
          }
        },
        onRequestError({ request, options, error }) {
          // console.log('--onRequestError---', error)
          if (process.server) {
            Logger.error(
              `${event?.context.$routeId}-${sysFlag}端-接口请求错误：`,
              JSON.stringify({
                request: request,
                params: options.params,
                method: options.method,
                headers: options.headers,
                body: options.body,
                error: error
              })
            )
          }
          // 处理请求错误
        },
        onResponse({ request, response, options }) {
          const { $loading }: any = useNuxtApp()
          const { status, _data } = response

          // 没有权限
          if (status == 401) {
            if (process.client) {
              ;(useAuthStore() as any).logout()
              trackEvent({ type: 'clklog', category: 'Logout' })
              showToast('登录已过期，请重新登录')
            }
          }
          // 其他错误
          if (_data.code == 400) {
            if (process.client) {
              $loading.hideLoading()
              showToast(_data.msg)
            }
          }
          // console.log('---onResponse---')
        },
        onResponseError({ request, response, options }) {
          // 接口异常
          const { msg, code } = response._data
          if (code == 401) return
          showToast(msg || 'system error')
        }
      }
      // 请求参数处理
      if (method === 'GET' || method === 'DELETE') {
        newOptions.params = reqData
      }
      if (method === 'POST' || method === 'PUT') {
        if (url.includes('/token')) {
          newOptions.params = reqData
        } else {
          newOptions.body = reqData
        }
      }
      if (process.client) {
        url = globalStor.currtClientApi + '/api' + url
      } else {
        if (options?.isJson) {
          url = globalStor.jsonApi + url
        } else {
          url = globalStor.serverApi + '/api' + url
        }
      }
      // useMsgLog(`${sysFlag}端-接口请求：${url}`)
      try {
        // 开启报文加密
        if (newOptions.cryptoData && process.client) {
          if (newOptions.params) {
            const data = crypto.encryptAES(JSON.stringify(newOptions.params), crypto.aesKey)
            newOptions.params = { data }
          }
          if (newOptions.body) {
            newOptions.body = crypto.encryptAES(JSON.stringify(removeRefs(newOptions.body)), crypto.aesKey)
          }
        }
        // 从 useFetch 获取数据和相关状态
        const { data, pending, error, refresh, status } = await useFetch(singleSlashPath(url), newOptions)
        // console.log(`${sysFlag}端-接口请求状态：${url}`, status.value)

        // 解析加密报文
        let newData = data.value
        console.log(newData, 'newData')
        // 根据不同的状态处理逻辑
        if (status.value === 'success') {
          if (sysFlag == 'Server') {
            if (process.server) {
              if (globalStor.isDev) {
                console.log(`dev-${sysFlag}端-接口请求成功：${url}`)
              }
              Logger.info(
                `${event?.context.$routeId}-${sysFlag}端-接口请求成功：`,
                JSON.stringify({
                  url: url
                  // data:newData
                })
              )
            }
          } else {
            if (newOptions.cryptoData && typeof newData === 'string') {
              newData = JSON.parse(crypto.decryptAES(data.value as string, crypto.aesKey))
            }
            // console.log(`${sysFlag}端-接口请求成功：${url}`)
          }
          if (newData == '[]') newData = []
          resolve(newData)
        } else if (status.value === 'error') {
          //console.log(`${sysFlag}端-接口请求失败：${url}`, error.value)
          if (process.server) {
            Logger.info(
              `${event?.context.$routeId}-${sysFlag}端-接口请求失败：`,
              JSON.stringify({
                url: url,
                data: error.value
              })
            )
          }
          reject(error.value)
        }
        // 如果请求处于其他状态，例如 pending 或 idle，可以根据需要进行处理
        if (pending) {
          // console.log('数据正在加载中...', url)
        }
      } catch (error) {
        console.error('异常捕获：', error)
        reject(error)
      }
    })
  }

  // 封装常用方法

  get<T = any>(url: string, params?: any, options?: Options<T>) {
    let questWay: Methods = 'POST'
    ;[].forEach((item) => {
      if (url.indexOf(item) > -1) {
        questWay = 'GET'
      }
    })
    return this.request(url, questWay, params, options)
  }

  post<T = any>(url: string, data: any, options?: Options<T>) {
    return this.request(url, 'POST', data, options)
  }

  Put<T = any>(url: string, data: any, options?: Options<T>) {
    return this.request(url, 'PUT', data, options)
  }

  Delete<T = any>(url: string, params: any, options?: Options<T>) {
    return this.request(url, 'DELETE', params, options)
  }

  // 获取 .json 文件
  async getJson<T = any>(url: string, params?: any, options?: Options<T>) {
    try {
      // 使用 GET 请求方法获取 .json 文件
      const response = await this.get<T>(url, params, {
        ...options,
        headers: {
          ...(options?.headers || {}),
          Accept: 'application/json' // 确保请求的内容类型是 JSON
        }
      })
      return response
    } catch (error) {
      console.error('获取 .json 文件时发生错误：', error)
      throw error
    }
  }
}

export const httpRequest = new HttpRequest()
